/**
 * SR-NET-05 진단 순서(FS-C1-08) — ① HTTPS 왕복(제어 평면 핑 5번) ② 진단 세션 → 룸 서버 공개 WSS(Colyseus create("netcheck") — 룸과 같은 길)
 * ③ WSS 왕복(에코 n번 · 중앙값 · p90) ④ 내려받기(서버가 16 KB 조각 × n) ⑤ 올리기(3 KB 조각 × n → 서버가 받은 바이트 · 시간) → 판정 · 보고.
 * WSS 가 안 붙으면(거부 · 시간 초과) ③~⑤ 는 건너뛰고 '어려움 — 혼자 놀이터는 된다'(FE-303 변형).
 * 시간원(now) · 연결(connect)은 주입 — 시험 · 가짜 모드가 바꾼다.
 */
import { NETCHECK, NETCHECK_LIMITS } from "@picnic-field/room-protocol";
import type { NetcheckApi } from "../api/netcheckApi";
import type {
  NetcheckConnect,
  NetcheckResult,
  NetcheckSocket,
  StepKey,
  StepState,
  Verdict,
  WssResult,
} from "../types";
import { kbps, levels, median, percentile, portOf, singlePort, verdictOf } from "./measure";

export interface RunDeps {
  api: NetcheckApi;
  connect: NetcheckConnect;
  /** 셸 주소(단일 포트 판정) */
  pageUrl: string;
  now?: () => number;
  onStep?: (k: StepKey, s: StepState) => void;
  signal?: AbortSignal;
}

const HTTPS_PINGS = 5;

class Timeout extends Error {}

function within<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((ok, no) => {
    const t = setTimeout(() => no(new Timeout("timeout")), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        ok(v);
      },
      (e: unknown) => {
        clearTimeout(t);
        no(e);
      },
    );
  });
}

const stepOf = (v: Verdict): StepState => (v === "pass" ? "done" : v);

export async function runNetcheck(d: RunDeps): Promise<NetcheckResult> {
  const now = d.now ?? (() => performance.now());
  const step = d.onStep ?? (() => {});
  const aborted = () => {
    if (d.signal?.aborted) throw new DOMException("aborted", "AbortError");
  };

  // ① HTTPS
  step("https", "active");
  const https: number[] = [];
  for (let i = 0; i < HTTPS_PINGS; i++) {
    aborted();
    const t0 = now();
    try {
      await d.api.ping();
      https.push(now() - t0);
    } catch {
      break;
    }
  }
  const httpsRttMs = median(https);
  step("https", httpsRttMs == null ? "fail" : "done");

  // ② 세션 · WSS
  step("wss", "active");
  let wss: WssResult;
  let sock: NetcheckSocket | null = null;
  let wsUrl: string | null = null;
  let session: Awaited<ReturnType<NetcheckApi["session"]>> | null = null;
  try {
    session = await d.api.session();
    wsUrl = session.wsUrl;
    sock = await within(d.connect(session.wsUrl, session.token), session.plan.timeoutMs);
    wss = "ok";
  } catch (e) {
    wss = e instanceof Timeout ? "timeout" : "blocked";
  }
  step("wss", wss === "ok" ? "done" : "fail");

  let rtts: number[] = [];
  let down: number | null = null;
  let up: number | null = null;
  if (sock && session) {
    const s = sock;
    const timeoutMs = session.plan.timeoutMs;
    try {
      // ③ 왕복
      step("rtt", "active");
      rtts = await within(echoes(s, session.plan.pings, now, aborted), timeoutMs);
      // ④ 내려받기
      step("down", "active");
      down = await within(download(s, session.plan.downKb, now), timeoutMs).catch(() => null);
      // ⑤ 올리기
      step("up", "active");
      up = await within(upload(s, session.plan.upKb, now), timeoutMs).catch(() => null);
    } catch (e) {
      if ((e as Error).name === "AbortError") throw e;
    } finally {
      await s.leave().catch(() => {});
    }
  }
  const rttMs = median(rtts);
  const r = {
    rttMs: rttMs == null ? null : Math.round(rttMs * 10) / 10,
    downKbps: down,
    upKbps: up,
  };
  const th = session?.thresholds;
  const lv = levels(r, th);
  if (wss === "ok") {
    step("rtt", rtts.length ? stepOf(lv.rtt) : "fail");
    step("down", down == null ? "fail" : stepOf(lv.down));
    step("up", up == null ? "fail" : stepOf(lv.up));
  } else for (const k of ["rtt", "down", "up"] as const) step(k, "skip");

  const p90 = percentile(rtts, 90);
  const result: NetcheckResult = {
    httpsRttMs: httpsRttMs == null ? null : Math.round(httpsRttMs * 10) / 10,
    wss,
    rttMs: r.rttMs,
    rttP90Ms: p90 == null ? null : Math.round(p90 * 10) / 10,
    downKbps: down,
    upKbps: up,
    port443: singlePort(d.pageUrl, wsUrl),
    httpsPort: portOf(d.pageUrl) ?? 443,
    wssPort: wsUrl ? portOf(wsUrl) : null,
    wsHost: wsUrl
      ? (() => {
          try {
            return new URL(wsUrl).host;
          } catch {
            return null;
          }
        })()
      : null,
    verdict: verdictOf(wss, r, th),
    levels: lv,
  };
  // 서버가 같은 기준으로 다시 매긴 판정(텔레메트리 — 기관 단위 · 비식별). 못 보내도 화면 판정은 그대로
  const server = await d.api.report(result);
  return server ? { ...result, verdict: server } : result;
}

/** 에코 n번 — 8바이트(순번 + 시각) · 한 번에 하나 */
async function echoes(
  s: NetcheckSocket,
  n: number,
  now: () => number,
  aborted: () => void,
): Promise<number[]> {
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    aborted();
    const probe = new Uint8Array(8);
    new DataView(probe.buffer).setUint32(0, i, true);
    const t0 = now();
    await new Promise<void>((ok) => {
      const off = s.onBytes(NETCHECK.echo, (b) => {
        if (
          b.byteLength >= 4 &&
          new DataView(b.buffer, b.byteOffset, b.byteLength).getUint32(0, true) === i
        ) {
          off();
          ok();
        }
      });
      s.sendBytes(NETCHECK.echo, probe);
    });
    out.push(now() - t0);
  }
  return out;
}

/** 내려받기 — 요청부터 끝 알림까지(왕복 하나 포함 — 보수적 추정) */
async function download(s: NetcheckSocket, kb: number, now: () => number): Promise<number | null> {
  let got = 0;
  const t0 = now();
  const offBlob = s.onBytes(NETCHECK.blob, (b) => void (got += b.byteLength));
  try {
    await new Promise<void>((ok) => {
      const off = s.onJson(NETCHECK.downEnd, () => {
        off();
        ok();
      });
      s.send(NETCHECK.down, { kb: Math.min(kb, NETCHECK_LIMITS.downKb) });
    });
  } finally {
    offBlob();
  }
  return kbps(got, now() - t0);
}

/** 올리기 — 첫 조각을 보낸 때부터 서버 결과를 받을 때까지(서버가 받은 바이트) */
async function upload(s: NetcheckSocket, kb: number, now: () => number): Promise<number | null> {
  const size = NETCHECK_LIMITS.upChunkBytes - 72; // 틀(Colyseus 머리) 여유
  const total = Math.min(kb, NETCHECK_LIMITS.upKb) * 1024;
  const chunk = new Uint8Array(size);
  for (let i = 0; i < size; i++) chunk[i] = (i * 131 + 7) & 0xff;
  const t0 = now();
  const res = new Promise<{ bytes: number }>((ok) => {
    const off = s.onJson(NETCHECK.upResult, (m) => {
      off();
      ok(m as { bytes: number });
    });
  });
  for (let sent = 0; sent < total; sent += size) s.sendBytes(NETCHECK.up, chunk);
  s.send(NETCHECK.upEnd, {});
  const r = await res;
  return kbps(Number(r.bytes) || 0, now() - t0);
}
